import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Wrench, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import ActorSelector from "@/components/planner/ActorSelector";
import PlannerFields from "@/components/planner/PlannerFields";
import TemplateLibrary from "@/components/planner/TemplateLibrary";
import PlaybookV2 from "@/components/planner/PlaybookV2";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const DEMO_PROBLEM = "Tumpukan sampah liar di bantaran sungai RW 05";

const QUICK_EXAMPLES = [
  { label: "Contoh Warga: Sampah liar menyumbat saluran air RT", text: "Sampah liar menumpuk dan menyumbat saluran air di lingkungan RT kami sehingga menyebabkan genangan saat hujan", actor: "Warga / Individu", budget: "" },
  { label: "Contoh Komunitas: Penghijauan & bank sampah bantaran sungai", text: "Komunitas kami ingin membuat program penghijauan dan bank sampah di bantaran sungai yang gersang dan penuh sampah plastik", actor: "Komunitas / Relawan", budget: "" },
  { label: "Contoh CSR: Alokasi Rp 50 Juta program nutrisi anak pesisir", text: "Perusahaan kami ingin mengalokasikan dana CSR Rp 50 juta untuk program perbaikan nutrisi anak-anak kampung nelayan pesisir", actor: "Perusahaan / CSR", budget: "50000000" },
];

const EMPTY_FIELDS = { category: "", beneficiaries: "", volunteers: "", duration: "", resources: "" };

export default function SolutionPage() {
  const [params] = useSearchParams();
  const { t } = useLang();
  const [problem, setProblem] = useState("");
  const [actor, setActor] = useState("Komunitas / Relawan");
  const [budget, setBudget] = useState("");
  const [region, setRegion] = useState("");
  const [fields, setFields] = useState(EMPTY_FIELDS);
  const [outLang, setOutLang] = useState("id");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const autoRan = useRef(false);

  const setField = (k, v) => setFields((p) => ({ ...p, [k]: v }));

  const generate = async (text = problem, actorOverride = actor) => {
    if (text.trim().length < 10) {
      toast.error("Jelaskan masalahnya minimal 10 karakter.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/playbook", {
        problem: text,
        actor: actorOverride,
        budget: parseInt(budget || "0", 10),
        region,
        language: outLang,
        category: fields.category,
        beneficiaries: fields.beneficiaries,
        volunteers: parseInt(fields.volunteers || "0", 10),
        duration: fields.duration,
        resources: fields.resources,
      });
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal membuat playbook. Coba lagi.");
    } finally {
      setLoading(false);
    }
  };

  const pickTemplate = (tpl) => {
    setProblem(tpl.problem);
    setField("category", tpl.category);
    toast.success(`Template "${tpl.title_id}" dimuat — sesuaikan lalu generate.`);
  };

  useEffect(() => {
    if (params.get("demo") === "1" && !autoRan.current) {
      autoRan.current = true;
      setProblem(DEMO_PROBLEM);
      generate(DEMO_PROBLEM);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
      <span className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 text-xs font-bold px-4 py-1.5 rounded-full">
        <Wrench className="w-3.5 h-3.5" /> Social Action Planner
      </span>
      <h1 className="font-heading font-black text-3xl md:text-4xl text-slate-900 tracking-tight mt-4">
        {t("planner_title_1")}<br /><span className="text-emerald-600">{t("planner_title_2")}</span>
      </h1>
      <p className="text-slate-500 mt-3 max-w-2xl mb-8">{t("planner_sub")}</p>

      <TemplateLibrary onPick={pickTemplate} />

      <div className="bg-white rounded-2xl border border-slate-200 shadow-[0_8px_32px_rgba(0,0,0,0.04)] p-6">
        <ActorSelector actor={actor} setActor={setActor} budget={budget} setBudget={setBudget} region={region} setRegion={setRegion} />
        <PlannerFields fields={fields} setField={setField} outLang={outLang} setOutLang={setOutLang} />
        <label htmlFor="problem-input" className="text-sm font-bold text-slate-700 block mb-2">
          {t("planner_problem_label")}
        </label>
        <Textarea
          id="problem-input"
          data-testid="problem-input"
          value={problem}
          onChange={(e) => setProblem(e.target.value)}
          placeholder={t("planner_problem_label")}
          className="rounded-xl min-h-[110px] text-base"
        />
        <div className="flex flex-wrap gap-2 mt-3">
          {QUICK_EXAMPLES.map((ex, i) => (
            <button key={i} data-testid={`example-problem-${i}`}
              onClick={() => { setProblem(ex.text); setActor(ex.actor); setBudget(ex.budget); }}
              className="text-xs font-semibold px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 hover:bg-emerald-50 hover:text-emerald-700 transition-colors">
              {ex.label}
            </button>
          ))}
        </div>
        <Button
          data-testid="generate-playbook-btn"
          onClick={() => generate()}
          disabled={loading}
          className="w-full sm:w-auto rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-8 h-12 mt-5"
        >
          {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> {t("planner_generating")}</> : <><Wrench className="w-5 h-5 mr-2" /> {t("planner_generate")}</>}
        </Button>
      </div>

      {result && <PlaybookV2 result={result} region={region} volunteers={fields.volunteers} />}
    </div>
  );
}
